import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  Building2,
  Hammer,
  Heart,
  Play,
  RotateCcw,
  Trophy,
  Frown,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
} from "lucide-react";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { createCardQueue } from "@/lib/cardQueue";

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function getCardKey(card: Card): string {
  return card.id ?? `${(card.front || "").trim()}|${(card.back || "").trim()}`;
}

function generateOptions(currentCard: Card, allCards: Card[]): string[] {
  const correct = (currentCard.back || "").trim();
  const others = allCards.filter(
    (c) => c !== currentCard && (c.back || "").trim()
  );
  const uniqueBacks = [
    ...new Set(
      others.map((c) => (c.back || "").trim()).filter(Boolean)
    ),
  ].filter((b) => b !== correct);
  const shuffled = shuffleArray(uniqueBacks);
  const distractors: string[] = [];
  for (let i = 0; distractors.length < 3 && i < shuffled.length * 2; i++) {
    const pick = shuffled[i % Math.max(1, shuffled.length)];
    if (pick && !distractors.includes(pick)) distractors.push(pick);
  }
  while (distractors.length < 3 && uniqueBacks[0]) {
    distractors.push(uniqueBacks[0]);
  }
  return shuffleArray([correct, ...distractors.slice(0, 3)]);
}

type GameState = "intro" | "playing" | "won" | "lost";

const BLOCKS_PER_TOWER = 10;
const BASE_POINTS = 100;
const WORDS_PER_LEVEL = 3;
const TOWER_COMPLETE_DURATION_MS = 2000;
const TILT_DEG_MIN = 5;
const TILT_DEG_MAX = 10;
const COLLAPSE_ANGLE = 90;

interface BuiltBlock {
  front: string;
  back: string;
}

const TOTAL_SLOTS = BLOCKS_PER_TOWER;

const SLOT_COLORS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-cyan-500",
] as const;

interface TowerModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
  onCardResult?: (cardId: string, correct: boolean) => void;
}

export default function TowerMode({ cards, lang: deckLang, onBack, onProgressComplete, onCardResult }: TowerModeProps) {
  const { t } = useTranslation();
  const { isSoundEnabled, isVoiceEnabled, gameVolume, voiceVolume, toggleSound: storeToggleSound, toggleVoice, setGameVolume, setVoiceVolume } = useSoundStore();
  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );
  const totalWords = filteredCards.length;

  const [gameState, setGameState] = useState<GameState>("intro");
  const [health, setHealth] = useState(3);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(1);
  const [level, setLevel] = useState(1);
  const [builtBlocks, setBuiltBlocks] = useState<BuiltBlock[]>([]);
  const [tiltAngle, setTiltAngle] = useState(0);
  const [currentWord, setCurrentWord] = useState<Card | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [answeredCorrect, setAnsweredCorrect] = useState(false);
  const [wrongShake, setWrongShake] = useState(false);
  const [clearedCardIds, setClearedCardIds] = useState<Set<string>>(new Set());
  const [showTowerComplete, setShowTowerComplete] = useState(false);

  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const winMusicRef = useRef<HTMLAudioElement | null>(null);
  const loseMusicRef = useRef<HTMLAudioElement | null>(null);
  const cardQueueRef = useRef(createCardQueue());
  const poolRef = useRef<Card[]>([]);

  const remainingForPick = useMemo(
    () => filteredCards.filter((c) => !clearedCardIds.has(getCardKey(c))),
    [filteredCards, clearedCardIds]
  );
  const wordsCleared = clearedCardIds.size;

  useEffect(() => {
    return () => {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    const vol = isSoundEnabled ? gameVolume : 0;
    if (bgMusicRef.current) { bgMusicRef.current.volume = vol; if (!isSoundEnabled) bgMusicRef.current.pause(); }
    if (winMusicRef.current) { winMusicRef.current.volume = vol; if (!isSoundEnabled) winMusicRef.current.pause(); }
    if (loseMusicRef.current) { loseMusicRef.current.volume = vol; if (!isSoundEnabled) loseMusicRef.current.pause(); }
  }, [gameVolume, isSoundEnabled]);

  useEffect(() => {
    if (gameState === "won" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      winMusicRef.current?.pause();
      if (winMusicRef.current) {
        winMusicRef.current.currentTime = 0;
        winMusicRef.current
          .play()
          .catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState === "lost" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      loseMusicRef.current?.pause();
      if (loseMusicRef.current) {
        loseMusicRef.current.currentTime = 0;
        loseMusicRef.current
          .play()
          .catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState !== "playing") {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
    }
  }, [gameState, isSoundEnabled]);

  const toggleSound = useCallback(() => {
    storeToggleSound();
    if (!useSoundStore.getState().isSoundEnabled) {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
    } else if (gameState === "playing" && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.volume = gameVolume;
      bgMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
    }
  }, [gameState, storeToggleSound, gameVolume]);

  const loadNextWord = useCallback(
    (cardsToPickFrom?: Card[]) => {
      const pool = cardsToPickFrom ?? remainingForPick;
      poolRef.current = pool;
      const getPoolIndices = () => {
        const keys = new Set(poolRef.current.map((c) => getCardKey(c)));
        return filteredCards.map((_, i) => i).filter((i) => keys.has(getCardKey(filteredCards[i])));
      };
      const idx = cardQueueRef.current.getNext(filteredCards, getPoolIndices);
      if (idx !== null) {
        const card = filteredCards[idx];
        setCurrentWord(card);
        setOptions(generateOptions(card, filteredCards));
        const term = (card.front || "").trim();
        if (useSoundStore.getState().isVoiceEnabled && term) {
          void playAudio({ card, side: "front", deckLang: deckLang ?? "en", volume: voiceVolume });
        }
      } else {
        setCurrentWord(null);
        setOptions([]);
      }
      setAnsweredCorrect(false);
    },
    [remainingForPick, filteredCards, voiceVolume, deckLang]
  );

  const startGame = useCallback(() => {
    cardQueueRef.current.reset();
    setGameState("playing");
    setHealth(3);
    setScore(0);
    setCombo(1);
    setLevel(1);
    setBuiltBlocks([]);
    setTiltAngle(0);
    setClearedCardIds(new Set());
    setShowTowerComplete(false);
    loadNextWord();
    if (isSoundEnabled && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.play().catch(console.error);
    }
  }, [loadNextWord, isSoundEnabled]);

  const tryAgain = useCallback(() => {
    winMusicRef.current?.pause();
    if (winMusicRef.current) winMusicRef.current.currentTime = 0;
    loseMusicRef.current?.pause();
    if (loseMusicRef.current) loseMusicRef.current.currentTime = 0;
    startGame();
  }, [startGame]);

  const handleCorrect = useCallback(() => {
    if (gameState !== "playing" || answeredCorrect || !currentWord) return;
    if (currentWord.id && onCardResult) onCardResult(currentWord.id, true);
    setAnsweredCorrect(true);

    const cardKey = getCardKey(currentWord);
    const newCleared = new Set([...clearedCardIds, cardKey]);
    setClearedCardIds(newCleared);

    const newBlocks = [
      ...builtBlocks,
      { front: currentWord.front, back: (currentWord.back || "").trim() },
    ];
    const nextWordsCleared = wordsCleared + 1;
    if (totalWords > 0 && onProgressComplete) {
      const pct = Math.min(100, Math.round((nextWordsCleared / totalWords) * 100));
      onProgressComplete(pct);
    }
    const towerLevel = 1 + Math.floor(nextWordsCleared / BLOCKS_PER_TOWER);
    setLevel(towerLevel);

    const points = BASE_POINTS * combo * towerLevel;
    setScore((prev) => prev + points);
    setCombo((prev) => Math.min(prev + 1, 5));

    const nextRemaining = filteredCards.filter((c) => !newCleared.has(getCardKey(c)));

    if (newBlocks.length === BLOCKS_PER_TOWER && nextWordsCleared < totalWords) {
      setBuiltBlocks(newBlocks);
      setShowTowerComplete(true);
      setTimeout(() => {
        setShowTowerComplete(false);
        setBuiltBlocks([]);
        setTiltAngle(0);
        loadNextWord(nextRemaining);
      }, TOWER_COMPLETE_DURATION_MS);
      return;
    }

    if (nextWordsCleared === totalWords) {
      setBuiltBlocks(newBlocks);
      setGameState("won");
      return;
    }

    setBuiltBlocks(newBlocks);
    setTimeout(() => loadNextWord(nextRemaining), 400);
  }, [
    gameState,
    answeredCorrect,
    currentWord,
    builtBlocks,
    combo,
    wordsCleared,
    clearedCardIds,
    filteredCards,
    totalWords,
    loadNextWord,
    isVoiceEnabled,
    voiceVolume,
    deckLang,
    onProgressComplete,
    onCardResult,
  ]);

  const handleWrong = useCallback(() => {
    if (gameState !== "playing" || answeredCorrect) return;
    if (currentWord?.id && onCardResult) onCardResult(currentWord.id, false);
    setAnsweredCorrect(true);
    setCombo(1);
    setWrongShake(true);
    setTimeout(() => setWrongShake(false), 400);

    const tiltAmount =
      TILT_DEG_MIN + Math.random() * (TILT_DEG_MAX - TILT_DEG_MIN);
    const direction = Math.random() > 0.5 ? 1 : -1;
    const delta = tiltAmount * direction;

    setHealth((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setTiltAngle((a) => {
          const finalAngle = a + delta;
          return Math.abs(finalAngle) >= Math.abs(COLLAPSE_ANGLE)
            ? Math.sign(finalAngle) * COLLAPSE_ANGLE
            : finalAngle;
        });
        setTimeout(() => setGameState("lost"), 600);
        return 0;
      }
      setTiltAngle((a) => a + delta);
      return next;
    });

    if (health > 1) {
      setTimeout(loadNextWord, 500);
    }
  }, [gameState, answeredCorrect, health, loadNextWord, currentWord, onCardResult]);

  const handleSelectOption = useCallback(
    (option: string) => {
      if (!currentWord || answeredCorrect) return;
      const isCorrect = option === (currentWord.back || "").trim();
      if (isCorrect) {
        handleCorrect();
      } else {
        handleWrong();
      }
    },
    [currentWord, answeredCorrect, handleCorrect, handleWrong]
  );

  const [highScore, setHighScore] = useState(0);
  useEffect(() => {
    if ((gameState === "won" || gameState === "lost") && score > highScore) {
      setHighScore(score);
    }
  }, [gameState, score, highScore]);

  useEffect(() => {
    if (gameState === "won") {
      const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];
      const fire = (ratio: number, opts: confetti.Options = {}) => {
        confetti({
          ...opts,
          colors,
          origin: opts.origin ?? { y: 0.7 },
          particleCount: Math.floor(200 * ratio),
          shapes: ["circle", "square"],
          scalar: opts.scalar ?? 1.2,
        });
      };
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91 });
    }
  }, [gameState]);

  const topNavRow = (
    <div className="flex justify-between items-center w-full max-w-4xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] text-gray-700 dark:text-gray-300 font-medium transition-colors"
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("tower.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className="p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center"
          aria-label={
            isSoundEnabled ? t("tower.muteSound") : t("tower.unmuteSound")
          }
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          ) : (
            <VolumeX className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-gray-400 dark:text-gray-500"}`}
          aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );

  if (filteredCards.length < 4) {
    return (
      <>
        <audio ref={bgMusicRef} src="/tower-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/tower-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/tower-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <p className="text-lg font-medium text-[hsl(var(--foreground))]">
              {t("tower.needCards")}
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              {t("tower.addMoreCards")}
            </p>
          </div>
        </div>
      </>
    );
  }

  if (gameState === "intro") {
    return (
      <>
        <audio ref={bgMusicRef} src="/tower-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/tower-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/tower-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center w-full max-w-lg mx-auto py-12 px-6">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full scale-150" />
              <Building2
                className="relative w-20 h-20 text-amber-500"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-3">
              {t("tower.title")}
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] text-center mb-8 max-w-sm">
              {t("tower.subtitle")}
            </p>
            <button
              type="button"
              onClick={startGame}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--accent2))] hover:opacity-90 transition-all hover:scale-105 active:scale-95 shadow-lg"
            >
              <Play className="w-6 h-6" strokeWidth={2.5} />
              {t("tower.startGame")}
            </button>
          </div>
        </div>
      </>
    );
  }

  const statsEndScreen = (
    <div className="flex flex-col w-full max-w-lg mx-auto py-12 px-6">
      {gameState === "won" ? (
        <Trophy
          className="w-20 h-20 text-yellow-500 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      ) : (
        <Frown
          className="w-24 h-24 text-gray-400/80 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      )}
      <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-8">
        {gameState === "won"
          ? t("tower.victory")
          : t("tower.towerCollapsed")}
      </h1>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("tower.yourScore")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {score}
          </p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("tower.wordsCleared")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {wordsCleared}
          </p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("tower.yourLevel")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {level}
          </p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("tower.highScore")}
          </p>
          <p className="text-2xl font-bold text-amber-500">
            {Math.max(highScore, score)}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={tryAgain}
        className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--primary))] hover:opacity-90 transition-all w-full max-w-xs mx-auto"
      >
        <RotateCcw className="w-5 h-5" />
        {t("tower.tryAgain")}
      </button>
    </div>
  );

  if (gameState === "won" || gameState === "lost") {
    return (
      <>
        <audio ref={bgMusicRef} src="/tower-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/tower-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/tower-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          {statsEndScreen}
        </div>
      </>
    );
  }

  return (
    <>
      <audio ref={bgMusicRef} src="/tower-bg.mp3" loop preload="auto" />
      <audio ref={winMusicRef} src="/tower-win.mp3" preload="auto" />
      <audio ref={loseMusicRef} src="/tower-lose.mp3" preload="auto" />
      <div className="flex flex-col w-full max-w-4xl mx-auto h-[calc(100vh-8rem)] md:h-[700px] min-h-[560px]">
        {topNavRow}
        <div className="relative flex flex-col flex-1 min-h-0 w-full max-w-2xl mx-auto px-4">
          {/* Tower complete overlay */}
          {showTowerComplete && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-amber-500/90 dark:bg-amber-600/90 backdrop-blur-sm animate-fade-in"
              aria-live="polite"
            >
              <p className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                {t("tower.towerComplete")}
              </p>
            </div>
          )}

          {/* Stats header - locked at top */}
          <div className="shrink-0 flex flex-col gap-2 py-4">
            <div className="text-center text-sm font-medium text-[hsl(var(--muted-foreground))]">
              {t("tower.wordsProgress", { cleared: wordsCleared, total: totalWords })}
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-[hsl(var(--foreground))]">
                  {t("tower.score")}: {score}
                </span>
                {combo > 1 && (
                  <span className="text-lg font-bold text-amber-500">x{combo}</span>
                )}
                <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                  {t("tower.level")}: {level}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3].map((i) => (
                  <Heart
                    key={i}
                    fill={i <= health ? "currentColor" : "none"}
                    className={`w-6 h-6 ${
                      i <= health ? "text-red-500" : "text-gray-400 dark:text-gray-600"
                    }`}
                    strokeWidth={2}
                    aria-hidden
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Battlefield - crane + tower, fills remaining space */}
          <div className="flex-1 relative flex flex-col justify-end items-center w-full overflow-hidden min-h-0">
            {/* Crane area - rope + current word block */}
            <div
              className={`shrink-0 flex flex-col items-center justify-end transition-transform ${
                wrongShake ? "animate-shake" : ""
              }`}
            >
              <div
                className="w-1.5 h-14 bg-gradient-to-b from-gray-600 via-gray-500 to-gray-400 rounded-full mx-auto mb-0 flex-shrink-0"
                style={{ boxShadow: "inset 0 0 4px rgba(0,0,0,0.2)" }}
                aria-hidden
              />
              <div className="flex flex-col items-center justify-center w-full">
                <div className="flex items-center gap-2 mb-2">
                  <Hammer className="w-5 h-5 text-amber-500" strokeWidth={2} aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    {t("tower.word")}
                  </p>
                </div>
                <div
                  className="flex items-center justify-center h-14 w-48 rounded-xl bg-blue-500 text-white font-bold text-lg shadow-md"
                >
                  {currentWord?.front ?? "..."}
                </div>
              </div>
            </div>

            {/* Tower area - fixed skeleton with tilt applied to wrapper */}
            <div className="flex-1 min-h-0 flex justify-center py-4 px-8 overflow-y-auto overflow-x-hidden w-full">
              <div
                className="flex flex-col items-center gap-2 max-w-xs mx-auto origin-bottom p-4"
                style={{
                  transform: `rotate(${tiltAngle}deg)`,
                  transition: "transform 0.5s ease-in-out",
                }}
              >
                {/* Slots: index 0 = top, index 9 = bottom; bottom slots fill first */}
                {Array.from({ length: TOTAL_SLOTS }).map((_, slotIndex) => {
                  const blockIndex = TOTAL_SLOTS - 1 - slotIndex;
                  const isFilled = blockIndex < builtBlocks.length;
                  const block = isFilled ? builtBlocks[blockIndex] : null;
                  const colorClass = isFilled
                    ? SLOT_COLORS[blockIndex % SLOT_COLORS.length]
                    : null;
                  return (
                    <div
                      key={slotIndex}
                      className={`flex items-center justify-center h-14 w-48 rounded-xl ${
                        isFilled
                          ? `${colorClass} text-white font-bold shadow-md`
                          : "border-2 border-dashed border-slate-300 dark:border-slate-600 bg-transparent"
                      }`}
                    >
                      {isFilled && block ? block.front : ""}
                    </div>
                  );
                })}
                {/* Concrete foundation */}
                <div
                  className="w-48 h-4 rounded-b-lg bg-gray-400 dark:bg-gray-600 flex-shrink-0 mt-1"
                  aria-hidden
                />
              </div>
            </div>
          </div>

          {/* Answer buttons - docked at bottom */}
          <div className="shrink-0 w-full mt-auto pt-4">
            <div className="grid grid-cols-2 gap-4">
              {options.filter(Boolean).map((opt, idx) => (
                <button
                  key={`${opt}-${idx}`}
                  type="button"
                  onClick={() => handleSelectOption(opt)}
                  disabled={answeredCorrect}
                  className="min-h-[72px] px-6 py-4 rounded-xl font-semibold text-base text-left transition-all duration-200 border-2 border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-[hsl(var(--border))] disabled:hover:bg-[hsl(var(--bg3))] active:scale-[0.98]"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
